/**
 * GuideStage — the two characters' home, driven by the beats table (beats.ts) and nothing else.
 *
 * Mr. Clippy (the buyer) and Foxy (the expert) rest INSIDE host elements (the ledger pane's guide layer) and
 * fly between them across a page-level layer; a flight re-parents the DOM node (appendChild) so the canvas,
 * its loop and the eye tweens keep running. Flights are rAF transforms (motion.ts); one-shots are CSS on the
 * body (fox-guide.css). ONE MOVER AT A TIME: a global queue serialises moves; a new step move replaces the
 * pending step move, a boundary request (entry/exit) replaces the pending boundary request, never each other.
 *
 * The story lives in the ledger and plays ONCE per load, forward only (CHOREOGRAPHY.md "Once per load"): B1 at
 * pane ≥ 35%, B2 the greeting, steps as they activate (never rewinding), B12/B13 at step 14 — or as 280ms fades if
 * the reader jumps past the ledger first. After B13 Foxy is gone and a second node, the gutter float (sticky at 38%
 * of the viewport in the right gutter; docked bottom-right below 1200px), stands through every section, hero and
 * ledger included, going nowhere: a 6px drift, breathing, blinks, small in-place life every 8–14s and the eye
 * reactions B14–B21. Foxy exists only inside the ledger — the fox marks in the one-brain circle and the
 * infrastructure node are those sections' own animated marks (FoxyMark), never a travelling character.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Mail } from 'lucide-react';
import { Character, type CharView, type Who } from './Character';
import { GutterFloat } from './GutterFloat';
import type { Eyes, Persona, PresenterState } from './CharacterPresenter';
import { GUTTER, REST_VIEW, SECTIONS, SIZES, StageContext, base, eyeLine, unit, type Dir, type LedgerStepInfo, type StageApi, type Target } from './stage.shared';
export { useStage, type Dir, type LedgerStepInfo, type Reaction, type StageApi, type Target } from './stage.shared';
import { cues, type CueName } from './sounds';
import { after, fly, type P } from './motion';
import { DUR } from './easing';
import { logBeat, type BeatId } from './beats';
import { useMedia } from '@/hooks/useMedia';
import type { Clippy, Destination } from '@/data/session';
import './fox-guide.css';

export function GuideStage({ children }: { children: ReactNode }) {
  const layer = useRef<HTMLDivElement>(null);
  const railBox = useRef<HTMLDivElement>(null);
  const nodes = useRef<Record<Who, HTMLDivElement | null>>({ clippy: null, foxy: null });
  const levels = { clippy: useRef(0), foxy: useRef(0) };
  const pos = useRef<Record<Who, { host: HTMLElement | null; x: number; y: number; r: number; sh: number; cancel: (() => void) | null }>>({
    clippy: { host: null, x: 0, y: 0, r: 0, sh: 1, cancel: null },
    foxy: { host: null, x: 0, y: 0, r: 0, sh: 1, cancel: null },
  });
  const [views, setViews] = useState<Record<Who, CharView>>({ clippy: base('clippy'), foxy: base('foxy') });
  const viewsRef = useRef(views);
  viewsRef.current = views;
  // the gutter float: a second Mr. Clippy node that lives in the rail, so leaving and returning cross-fade
  const floatNode = useRef<HTMLDivElement | null>(null);
  const floatLevel = useRef(0);
  const [floatView, setFloatView] = useState<CharView>({ ...base('clippy'), act: 'float', facing: 'left', persona: { skeptic: 0, lean: 0, breath: 3.2 } });
  const floatRef = useRef(floatView);
  floatRef.current = floatView;
  const patchFloat = useCallback((p: Partial<CharView>) => setFloatView((v) => ({ ...v, ...p })), []);
  const storyEnded = useRef(false);
  const [floatGone, setFloatGone] = useState(false); // the reader hid him: gone for the load
  const draggingRef = useRef(false); // no in-place life while the reader drags him
  const emoted = useRef<Set<string>>(new Set()); // `${section}:${nod|pop|lean|blink}` — once per load
  const pendingOwner = useRef<string | null>(null); // a section that took his eyes while B13 was still in the air
  /** where he actually stood on each step (pane coordinates): a stay step keeps the previous spot, so a return must not use its nominal x/y */
  const paneSpots = useRef<Record<number, P>>({});
  const [foxyMounted, setFoxyMounted] = useState(false);
  const [reduced, setReduced] = useState(false);
  const narrowMedia = useMedia('(max-width: 1199px)');
  // the float docks bottom-right when the right gutter is narrower than 110px (he must never overlap content)
  const [gutterNarrow, setGutterNarrow] = useState(false);
  const narrow = narrowMedia || gutterNarrow;
  const [mail, setMail] = useState<{ key: number; gone: boolean } | null>(null);
  const mailRef = useRef<HTMLDivElement>(null);
  const mailFlight = useRef<{ from: P; c1: P; c2: P; to: P } | null>(null);
  const [rail, setRail] = useState<{ left: number; top: number; height: number } | null>(null);
  const sayKey = useRef(0);
  // `arrived` = B1 has started (never latched by a step activation); `arrivalPlayed` = B1 completed
  const arrived = useRef(false);
  const arrivalPlayed = useRef(false);
  const inGutter = useRef(false);
  const ledgerOwns = useRef(false);
  const lastSection = useRef<string | null>(null);
  const lastInfo = useRef<LedgerStepInfo | null>(null);
  const emoteUntil = useRef(0);
  const timers = useRef<number[]>([]);
  // B1 keeps its own timers: a step activation during the arc must never clear its landing, wave and completion
  const arrivalTimers = useRef<number[]>([]);
  const gen = useRef(0);
  const [ownerKey, setOwnerKey] = useState('');
  const eyeBeatDone = useRef<Set<string>>(new Set());
  const foxyAtCorner = useRef(false);
  const visibleRatios = useRef(new Map<string, number>());
  const sectionBeat = useRef<(sel: string) => void>(() => {});
  // the move queue: one mover at a time, at most one beat waiting
  const moving = useRef<Who | null>(null);
  const pending = useRef<{ who: Who; start: (done: () => void) => void; tag: 'step' | 'boundary' }[]>([]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
  }, []);
  // the gutter rail: from the funnel's top to the invitation's bottom, centred 72px left of the content column
  useEffect(() => {
    const measure = () => {
      const l = layer.current?.getBoundingClientRect();
      const invite = document.querySelector('.fx-invite')?.getBoundingClientRect();
      const box = document.querySelector('.fx-funnel .container')?.getBoundingClientRect();
      if (!l || !invite || !box) return;
      // the rail line is the RIGHT gutter: container right + 72px; it spans the whole page so the float, once up,
      // is visible through every section — the ledger and the hero included (once per load: he never comes back in)
      // the layer starts below the hero: the rail begins at the page's top (a negative offset in layer coordinates)
      const pageTop = l.top + window.scrollY;
      setRail({ left: box.right - l.left + GUTTER, top: -pageTop, height: invite.bottom + window.scrollY });
      setGutterNarrow(window.innerWidth - box.right < 110);
      // (the sticky box is centred on that line via its inline margin below)
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, []);

  const patch = useCallback((who: Who, p: Partial<CharView>) => {
    setViews((v) => ({ ...v, [who]: { ...v[who], ...p } }));
  }, []);
  const wait = useCallback((ms: number, fn: () => void) => {
    const id = after(ms, fn);
    timers.current.push(id);
    return id;
  }, []);
  const waitA = useCallback((ms: number, fn: () => void) => {
    const id = after(ms, fn);
    arrivalTimers.current.push(id);
    return id;
  }, []);
  const cue = useCallback((name: CueName, who: Who, exempt = false) => {
    cues[name]({ exempt, el: nodes.current[who] });
  }, []);
  const apply = useCallback((who: Who) => {
    const el = nodes.current[who];
    const p = pos.current[who];
    if (!el) return;
    const size = viewsRef.current[who].size;
    el.style.transform = `translate(${p.x - size / 2}px, ${p.y - size / 2}px) rotate(${p.r}deg)`;
    el.style.setProperty('--sh', `${p.sh}`);
  }, []);
  // a size change re-centres the transform on the anchor (a stay step after the pair shrinks him in place)
  useEffect(() => { apply('clippy'); }, [apply, views.clippy.size]);
  useEffect(() => { apply('foxy'); }, [apply, views.foxy.size]);
  const toLayer = useCallback((host: HTMLElement, x: number, y: number): P => {
    const l = layer.current!.getBoundingClientRect();
    const h = host.getBoundingClientRect();
    return { x: h.left - l.left + x, y: h.top - l.top + y };
  }, []);
  const dock = useCallback((who: Who, host: HTMLElement, x: number, y: number, r = 0) => {
    const el = nodes.current[who];
    if (!el) return;
    if (el.parentElement !== host) host.appendChild(el);
    pos.current[who] = { ...pos.current[who], host, x, y, r };
    document.dispatchEvent(new CustomEvent('fx-guide-dock', { detail: { who, host: host.className.split(' ')[0], x: Math.round(x), y: Math.round(y) } }));
    apply(who);
  }, [apply]);
  /** React removes a node from the parent it rendered it in: bring it home before an unmount. */
  const unmountFoxy = useCallback(() => {
    const el = nodes.current.foxy;
    if (el && layer.current && el.parentElement !== layer.current) layer.current.appendChild(el);
    pos.current.foxy = { host: null, x: 0, y: 0, r: 0, sh: 1, cancel: null };
    foxyAtCorner.current = false;
    setFoxyMounted(false);
  }, []);
  const whereInLayer = useCallback((who: Who): P => {
    const p = pos.current[who];
    if (!p.host || p.host === layer.current) return { x: p.x, y: p.y };
    return toLayer(p.host, p.x, p.y);
  }, [toLayer]);
  const oneShot = useCallback((who: Who, cls: string, ms: number) => {
    setViews((v) => ({ ...v, [who]: { ...v[who], motion: [...v[who].motion.filter((m) => m !== cls), cls] } }));
    wait(ms, () => setViews((v) => ({ ...v, [who]: { ...v[who], motion: v[who].motion.filter((m) => m !== cls) } })));
  }, [wait]);
  const emote = useCallback((who: Who, name: Clippy['emote'] | 'wave' | 'nod' | 'wink' | undefined, withCue = true) => {
    if (!name) return;
    if (name === 'wink') {
      patch(who, { eyes: { mode: 'wink', key: performance.now() } });
      return;
    }
    emoteUntil.current = performance.now() + DUR.m * 2;
    patch(who, { emote: name });
    if (withCue && who === 'clippy' && name !== 'listen') cue(name as CueName, who);
    wait(DUR.m * 2, () => patch(who, { emote: null }));
  }, [cue, patch, wait]);
  const say = useCallback((who: Who, text: string | undefined, delay = 0) => {
    if (!text) return;
    sayKey.current += 1;
    patch(who, { say: { text, key: sayKey.current, delay } });
    if (who === 'clippy') wait(delay, () => cue('bubble', who));
  }, [cue, patch, wait]);

  /** The queue: run `start` now if nobody moves, else keep it pending — one pending step move (the latest replaces an
   *  earlier one) and one pending boundary request (entry or exit; the latest wins), so a boundary crossing during a
   *  hop is never dropped by the hop's own follow-up moves. */
  const enqueue = useCallback((who: Who, start: (done: () => void) => void, tag: 'step' | 'boundary' = 'step') => {
    const run = (s: (done: () => void) => void, w: Who) => {
      moving.current = w;
      s(() => {
        moving.current = null;
        const next = pending.current.shift();
        if (next) run(next.start, next.who);
      });
    };
    if (moving.current) {
      pending.current = pending.current.filter((p) => p.tag !== tag);
      pending.current.push({ who, start, tag });
    } else run(start, who);
  }, []);

  /** Move a character to a target as a beat: a hop (450/700ms arc) or an appear (fade). */
  const moveTo = useCallback((who: Who, t: Target, kind: 'hop' | 'appear', beat: BeatId, onDone?: () => void, landState?: PresenterState, note?: string) => {
    gen.current += 1;
    const el = nodes.current[who];
    if (!el || !layer.current) return;
    enqueue(who, (done) => {
      const p = pos.current[who];
      p.cancel?.();
      p.cancel = null;
      const show = () => patch(who, { hidden: false });
      el.dataset.moving = '1';
      const finish = () => {
        delete el.dataset.moving;
        onDone?.();
        done();
      };
      document.dispatchEvent(new CustomEvent('fx-guide-move', { detail: { who, kind, beat, host: t.host.className.split(' ')[0], x: Math.round(t.x), y: Math.round(t.y) } }));
      if (kind === 'appear' || reduced) {
        logBeat(beat, `${who} appears${note ? ' · ' + note : ''}`, 'fade');
        patch(who, { hidden: true });
        // own timers (after, not wait): a step change during the fade clears the step timers and must not strand the queue
        after(DUR.s, () => {
          dock(who, t.host, t.x, t.y);
          if (landState) patch(who, { state: landState });
          void getComputedStyle(el).opacity;
          show();
          after(DUR.s, finish);
        });
        return;
      }
      show();
      const start = whereInLayer(who);
      const sameHost = p.host === t.host;
      const end = sameHost ? { x: t.x, y: t.y } : toLayer(t.host, t.x, t.y);
      const from = sameHost ? { x: p.x, y: p.y } : start;
      if (!sameHost) dock(who, layer.current!, start.x, start.y, p.r);
      const dist = Math.hypot(end.x - from.x, end.y - from.y);
      if (dist < 2) {
        dock(who, t.host, t.x, t.y);
        if (landState) patch(who, { state: landState });
        finish();
        return;
      }
      logBeat(beat, `${who} hops ${Math.round(dist)}px${note ? ' · ' + note : ''}`, 'move');
      const dir = end.x >= from.x ? 1 : -1;
      const prev = landState ?? viewsRef.current[who].state;
      if (prev === 'listening') patch(who, { state: 'thinking' });
      cue('hop', who);
      p.cancel = fly({
        from, to: end, ms: dist <= 220 ? DUR.l : DUR.xl, lift: Math.min(90, 40 + dist * 0.15), drift: 20 * dir, rFrom: p.r, rTo: 6 * dir, ease: 'in-out',
        onFrame: (q) => {
          pos.current[who] = { ...pos.current[who], x: q.x, y: q.y, r: q.r, sh: q.sh };
          apply(who);
        },
        onDone: () => {
          p.cancel = null;
          dock(who, t.host, t.x, t.y, 0);
          if (landState) patch(who, { state: landState });
          else if (viewsRef.current[who].state === 'thinking' && prev === 'listening') patch(who, { state: 'listening' });
          oneShot(who, 'land', DUR.m);
          cue('hopLand', who);
          finish();
        },
      });
    });
  }, [apply, cue, dock, enqueue, oneShot, patch, reduced, toLayer, wait, whereInLayer]);

  // ── the ledger ──
  const ledgerTargets = useCallback((info: LedgerStepInfo) => {
    const c = info.step.clippy!;
    const vw = info.viewEl.clientWidth;
    const vh = info.viewEl.clientHeight;
    if (info.slots) {
      const sc = info.slots.clippy.getBoundingClientRect();
      const sf = info.slots.foxy.getBoundingClientRect();
      const pr = info.paneHost.getBoundingClientRect();
      const clippy: Target = { host: info.paneHost, x: sc.left - pr.left + sc.width / 2, y: sc.top - pr.top + sc.height / 2 };
      const foxy: Target = { host: info.paneHost, x: sf.left - pr.left + sf.width / 2, y: clippy.y + eyeLine('clippy', SIZES.clippy.pair) - eyeLine('foxy', SIZES.foxy.pair) };
      return { clippy, foxy, pair: true };
    }
    // steps 1–4: Foxy stands at the greeting spot (centre-right, facing him) where the pair will compose;
    // from the Leads frame on she is at her driving corner
    const greet: Target = { host: info.paneHost, x: vw / 2 + 28 + SIZES.foxy.pair / 2, y: vh * 0.42 + eyeLine('clippy', SIZES.clippy.pair) - eyeLine('foxy', SIZES.foxy.pair) };
    return {
      clippy: { host: info.paneHost, x: (c.x / 100) * vw, y: (c.y / 100) * vh } as Target,
      foxy: info.index < 4 ? greet : ({ host: info.paneHost, x: vw - 52, y: vh - 48 } as Target),
      pair: false,
    };
  }, []);
  const applyStepViews = useCallback((info: LedgerStepInfo, pair: boolean) => {
    const { step } = info;
    const c = step.clippy!;
    const narrated = step.flow?.status === 'narrated';
    const clippyState: PresenterState = step.kind === 'buyer' ? 'speaking' : step.kind === 'foxet' && narrated ? 'thinking' : 'listening';
    const foxyState: PresenterState = step.kind === 'foxet' ? (narrated ? 'thinking' : 'speaking') : 'listening';
    const toward = c.target ? unit(c.target.x - c.x, c.target.y - c.y) : pair ? { x: 1, y: 0 } : { x: 0, y: 0 };
    const facing: 'left' | 'right' = pair ? 'right' : c.target && c.target.x < c.x ? 'left' : 'right';
    const key = `${info.index}`;
    const clippyEyes: Eyes = c.eyes ? { mode: c.eyes, target: toward, key } : c.target || pair ? { mode: 'look', target: toward, key } : { mode: 'neutral', key };
    // at the greeting spot (steps 1–4) she looks at what he does: the CTA, the form field, the Leads row
    const foxyToward = pair ? { x: -1, y: 0 } : info.index < 4 ? (c.target ? unit(c.target.x - 62, c.target.y - 42) : { x: -1, y: 0.4 }) : c.target ? unit(c.target.x - 90, c.target.y - 86) : { x: -1, y: -0.3 };
    const foxyEyes: Eyes = step.foxyEyes ? { mode: step.foxyEyes, target: foxyToward, key } : { mode: 'look', target: foxyToward, key };
    patch('clippy', {
      state: clippyState, eyes: clippyEyes, persona: { skeptic: c.skeptic ?? 0, lean: c.lean ?? 0, breath: c.breath ?? 3 }, facing,
      size: pair ? SIZES.clippy.pair : SIZES.clippy.frame, action: c.action, act: c.act, tilt: c.action === 'point' ? 8 * (facing === 'right' ? 1 : -1) : 0, lean: c.lean ?? 0,
      badge: c.act === 'qualified' || c.act === 'exit' ? viewsRef.current.clippy.badge : false, docked: true, say: null,
    });
    patch('foxy', {
      state: foxyState, eyes: foxyEyes, persona: { breath: 3 }, facing: 'left', size: pair ? SIZES.foxy.pair : SIZES.foxy.frame, action: 'idle', act: c.act, tilt: 0, lean: 0, docked: true,
      say: performance.now() < welcomeUntil.current ? viewsRef.current.foxy.say : null,
    });
    if (step.kind === 'foxet' && info.index >= 4) cue('mm', 'foxy');
    return { clippyState, foxyState };
  }, [cue, patch]);

  const playStepRef = useRef<(info: LedgerStepInfo) => void>(() => {});
  const greetRef = useRef<() => void>(() => {});
  const welcomed = useRef(false);
  const welcomeUntil = useRef(0); // the greeting bubble is held for 1.4s; a step line that lands sooner waits for it
  const foxyMountedRef = useRef(false);
  foxyMountedRef.current = foxyMounted;

  // ── once per load, forward only (CHOREOGRAPHY.md "Once per load") ──
  // The story plays the first time the reader scrolls down through the ledger: B1 at pane ≥ 35%, steps as they
  // activate (the beat state only advances; a lower step never rewinds anyone), B12/B13 when step 14 activates.
  // After B13 Foxy is gone for the rest of the load and Mr. Clippy floats in the right gutter through every
  // section, ledger and hero included. Scrolling up changes nothing. A jump past the ledger before B13 fires
  // B12/B13 at once as 280ms fades. A reload starts over.
  const maxStep = useRef(-1);
  const whenFoxyMounted = useCallback((fn: () => void) => { let n = 0; const tick = () => { if (nodes.current.foxy || n > 30) fn(); else { n += 1; requestAnimationFrame(tick); } }; tick(); }, []);
  const unhideInstant = useCallback((who: Who) => { const el = nodes.current[who]; if (el) void getComputedStyle(el).opacity; }, []);
  const hideInstant = useCallback((who: Who) => {
    const el = nodes.current[who];
    if (el) el.style.transition = 'none';
    patch(who, { hidden: true });
    after(50, () => { if (el) el.style.transition = ''; });
  }, [patch]);

  /** B1: in from the top-left gutter on a parabolic arc down and to the right (once per load). */
  const arrive = useCallback((info: LedgerStepInfo) => {
    if (arrived.current || !layer.current) return;
    arrived.current = true;
    lastInfo.current = info;
    const { pair } = ledgerTargets(info);
    applyStepViews(info, pair);
    const first = info.index === 0;
    const tc: Target = { host: info.paneHost, x: (info.landing.x / 100) * info.viewEl.clientWidth, y: (info.landing.y / 100) * info.viewEl.clientHeight };
    const c = info.step.clippy!;
    // after the landing: the current step's hop (one mover at a time: it queues behind B1) and Foxy if due
    const continueToStep = () => {
      arrivalPlayed.current = true;
      paneSpots.current[0] = { x: tc.x, y: tc.y };
      // the reader may have scrolled on during B1: continue to whatever step is current now
      const now = lastInfo.current;
      if (now && now.index !== 0) playStepRef.current(now);
    };
    if (reduced) {
      logBeat('B1', 'reduced motion: appears');
      dock('clippy', tc.host, tc.x, tc.y);
      patch('clippy', { hidden: false, eyes: { mode: 'wide', key: 'arrive' } });
      if (first) say('clippy', c.say);
      greetRef.current();
      continueToStep();
      return;
    }
    const l = layer.current.getBoundingClientRect();
    const cr = info.containerEl.getBoundingClientRect();
    const sr = info.sectionEl.getBoundingClientRect();
    const from = { x: cr.left - l.left - GUTTER, y: sr.top - l.top - 80 };
    const to = toLayer(tc.host, tc.x, tc.y);
    enqueue('clippy', (done) => {
      logBeat('B1', `arrival from (${Math.round(from.x)}, ${Math.round(from.y)})`);
      dock('clippy', layer.current!, from.x, from.y, 25);
      pos.current.clippy.sh = 0;
      apply('clippy');
      patch('clippy', { hidden: false, state: 'thinking', eyes: { mode: 'squint', key: 'arrive' } });
      cue('whoosh', 'clippy', true);
      pos.current.clippy.cancel = fly({
        from, to, ms: DUR.xxl, lift: 140, drift: 60, rFrom: 25, rTo: -10, ease: 'in-out',
        onFrame: (q) => {
          pos.current.clippy = { ...pos.current.clippy, x: q.x, y: q.y, r: q.r, sh: q.sh };
          apply('clippy');
        },
        onDone: () => {
          pos.current.clippy.cancel = null;
          dock('clippy', tc.host, tc.x, tc.y, 0);
          patch('clippy', { state: 'listening', eyes: { mode: 'wide', key: 'landed' } });
          setViews((v) => ({ ...v, clippy: { ...v.clippy, dust: v.clippy.dust + 1 } }));
          oneShot('clippy', 'bounce', DUR.xl);
          cue('land', 'clippy', true);
          oneShot('clippy', 'squash', 80);
          waitA(290, () => { oneShot('clippy', 'squash', 80); cue('tap', 'clippy', true); });
          waitA(520, () => { oneShot('clippy', 'squash', 80); cue('tap', 'clippy', true); });
          waitA(DUR.xl, () => {
            patch('clippy', { eyes: { mode: 'squint', key: 'settled' } });
            emote('clippy', 'wave');
            if (first) say('clippy', c.say, DUR.s);
            done();
            // B2: 450ms after the settle Foxy fades up at the greeting spot, eyes on him, "Welcome." — then, if the
            // arrival landed mid-ledger, he catches up to the current step (B2 always precedes B3)
            waitA(DUR.l, () => { greetRef.current(); waitA(DUR.s, continueToStep); });
          });
        },
      });
    });
  }, [apply, applyStepViews, cue, dock, emote, enqueue, ledgerTargets, oneShot, patch, reduced, say, toLayer, waitA]);

  const gutterTarget = useCallback((): Target | null => {
    const box = railBox.current;
    if (!box) return null;
    const size = narrow ? SIZES.clippy.dock : SIZES.clippy.frame;
    return { host: box, x: size / 2, y: size / 2 };
  }, [narrow]);

  /** B12 + B13 (once): out of the pane's bottom-right, on to the right gutter; Foxy waves and is gone for the load.
   *  `soft` (reduced motion, or the pane left / step 14 activated before the story got there): 280ms fades instead. */
  const exit = useCallback((info: LedgerStepInfo, soft: boolean) => {
    if (!layer.current || storyEnded.current) return;
    const rest = gutterTarget();
    if (!rest) return;
    storyEnded.current = true;
    inGutter.current = true;
    const c = info.step.clippy;
    patch('clippy', { eyes: { mode: 'exit', key: 'exit' }, say: null });
    const showFloat = (bounce: boolean) => {
      patchFloat({ ...REST_VIEW, hidden: false, size: narrow ? SIZES.clippy.dock : SIZES.clippy.frame, badge: viewsRef.current.clippy.badge });
      floatRef.current = { ...floatRef.current, hidden: false };
      const owner = pendingOwner.current ?? (ledgerOwns.current ? null : lastSection.current);
      if (owner) after(bounce ? DUR.l * 2 : DUR.m, () => sectionBeat.current(owner));
      if (!bounce) return;
      setFloatView((v) => ({ ...v, motion: ['bounce2'] }));
      wait(DUR.l, () => setFloatView((v) => ({ ...v, motion: ['exhale'] })));
      wait(DUR.l * 2, () => setFloatView((v) => ({ ...v, motion: [] })));
    };
    if (soft || reduced) {
      // Foxy fades first, then him; the float fades in where it stands
      logBeat('B12', `${reduced ? 'reduced motion' : 'the pane left before the story got there'}: fades out`, 'fade');
      if (foxyMountedRef.current) { patch('foxy', { hidden: true, say: null }); after(DUR.m, unmountFoxy); }
      after(foxyMountedRef.current ? DUR.m : 0, () => {
        patch('clippy', { hidden: true });
        after(DUR.m, () => {
          logBeat('B13', 'the float fades in in the right gutter', 'fade');
          showFloat(false);
        });
      });
      return;
    }
    if (foxyMountedRef.current) {
      emote('foxy', 'wave', false);
      wait(DUR.xl, () => { patch('foxy', { hidden: true }); wait(DUR.m, unmountFoxy); });
    }
    enqueue('clippy', (done) => {
      oneShot('clippy', 'windup', DUR.s);
      wait(DUR.s, () => {
        const start = whereInLayer('clippy');
        dock('clippy', layer.current!, start.x, start.y, 0);
        patch('clippy', { state: 'thinking', act: 'exit' });
        const pane = (info.viewEl.closest('.fx-browser') ?? info.viewEl).getBoundingClientRect();
        const l = layer.current!.getBoundingClientRect();
        const br = { x: pane.right - l.left + 120, y: pane.bottom - l.top + 80 };
        const end = toLayer(rest.host, rest.x, rest.y);
        const onFrame = (q: { x: number; y: number; r: number; sh: number }) => {
          pos.current.clippy = { ...pos.current.clippy, x: q.x, y: q.y, r: q.r, sh: q.sh };
          apply('clippy');
        };
        logBeat('B12', `exit from the pane to (${Math.round(br.x)}, ${Math.round(br.y)})`);
        cue('whoosh', 'clippy');
        pos.current.clippy.cancel = fly({
          from: start, to: br, ms: DUR.xxl, lift: 180, drift: 120, rFrom: -10, rTo: 35, ease: 'in-out', onFrame,
          onDone: () => {
            logBeat('B13', `on to the right gutter (${Math.round(end.x)}, ${Math.round(end.y)})`);
            pos.current.clippy.cancel = fly({
              from: br, to: end, ms: DUR.xl, lift: 80, drift: 60, rFrom: 35, rTo: 0, ease: 'in-out', onFrame,
              onDone: () => {
                pos.current.clippy.cancel = null;
                // land on the float's spot; the float takes over at rest (same pose, no travel) with the bounce and exhale
                dock('clippy', rest.host, rest.x, rest.y, 0);
                showFloat(true);
                cue('hopLand', 'clippy');
                hideInstant('clippy');
                done();
              },
            });
          },
        });
        if (c?.followUpEmail) {
          wait(220, () => {
            const m = info.mailFromEl?.getBoundingClientRect();
            const from = m ? { x: m.left - l.left + m.width / 2, y: m.top - l.top + m.height / 2 } : start;
            mailFlight.current = { from, c1: { x: from.x + 160, y: from.y - 240 }, c2: { x: br.x + 40, y: br.y - 40 }, to: { x: br.x + 160, y: br.y + 60 } };
            setMail({ key: performance.now(), gone: false });
            cue('whooshLight', 'clippy');
          });
        }
      });
    });
  }, [apply, cue, dock, emote, enqueue, gutterTarget, hideInstant, narrow, oneShot, patch, patchFloat, reduced, toLayer, unmountFoxy, wait, whereInLayer]);

  const playStep = useCallback((info: LedgerStepInfo) => {
    lastInfo.current = info;
    const c = info.step.clippy;
    if (!c || !layer.current) return;
    const last = info.index === info.total - 1;
    if (storyEnded.current) return; // after B13 nothing in the ledger moves anyone
    if (!arrivalPlayed.current) return; // B1 first: the ledger calls arrive() once the pane is seen, and B1 continues here
    if (info.index <= maxStep.current) return; // forward only: a lower step never rewinds; the story resumes at the next step
    maxStep.current = info.index;
    timers.current.forEach(clearTimeout);
    timers.current = [];
    const { clippy: tc, foxy: tf, pair } = ledgerTargets(info);
    const { clippyState, foxyState } = applyStepViews(info, pair);
    // steps 1–14 → beats: the arrival spot, B3 CTA, B4 form, B5 lead, B6 greeting (×2), B7 Leads frame, B8 Hindi ask/switch, B8 export ask, B9 not verified, B10 draft, B11 Salesforce, B12 exit
    const beat = (['B3', 'B3', 'B4', 'B5', 'B6', 'B6', 'B7', 'B8', 'B8', 'B8', 'B9', 'B10', 'B11', 'B12'] as BeatId[])[Math.min(info.index, 13)] ?? 'B3';
    if (c.act === 'exit' && last) {
      // step 14: "Book me." where he stands, then B12/B13 — the arcs when the pane is in view, 280ms fades when the
      // reader jumped past the ledger (the active-step picker names step 14 once the whole ledger is above the viewport)
      const pr = info.paneHost.getBoundingClientRect();
      const visible = Math.max(0, Math.min(pr.bottom, window.innerHeight) - Math.max(pr.top, 0)) / pr.height;
      if (visible < 0.35) { exit(info, true); return; }
      patch('clippy', { state: clippyState, badge: viewsRef.current.clippy.badge });
      say('clippy', c.say);
      const g = gen.current;
      wait(DUR.l, () => { if (gen.current !== g) return; exit(info, false); });
      return;
    }
    const kind = viewsRef.current.clippy.hidden ? 'appear' : 'hop';
    const landed = () => {
      paneSpots.current[info.index] = { x: pos.current.clippy.x, y: pos.current.clippy.y };
      if (c.act === 'qualified') {
        say('clippy', c.say, DUR.s);
        wait(DUR.xl + DUR.l, () => {
          logBeat('B11', 'victory hop in place');
          patch('clippy', { badge: true, eyes: { mode: 'sparkle', key: 'badge' } });
          oneShot('clippy', 'victory', DUR.l);
          cue('cheer', 'clippy');
          emote('foxy', 'nod', false);
          emote('foxy', 'wink', false);
          say('clippy', 'Alright. I’m in.', DUR.l);
        });
      } else if (c.emote === 'think') {
        logBeat('B9', 'flinch then nod in place');
        oneShot('clippy', 'flinch', DUR.s);
        wait(DUR.s, () => emote('clippy', 'nod'));
        say('clippy', c.say, DUR.m);
      } else {
        if (info.index >= 7 && info.index <= 9) logBeat('B8', 'eyes and speech only');
        emote('clippy', c.emote);
        say('clippy', c.say, DUR.s);
      }
      if (c.emote === 'curious') patch('clippy', { eyes: { mode: 'double-blink', key: `${info.index}` } });
    };
    // Foxy: at the greeting spot from B2 (steps 1–4), the pair at the greeting, her corner from the Leads frame (B7)
    const foxyBeats = () => {
      const corner = !pair && info.index >= 4;
      if (!foxyMounted || viewsRef.current.foxy.hidden) {
        setFoxyMounted(true);
        wait(16, () => whenFoxyMounted(() => {
          logBeat(corner ? 'B7' : 'B6', corner ? 'Foxy appears at her corner' : 'Foxy fades up at the greeting');
          dock('foxy', tf.host, tf.x, tf.y);
          if (nodes.current.foxy) void getComputedStyle(nodes.current.foxy).opacity;
          patch('foxy', { hidden: false, state: foxyState });
          foxyAtCorner.current = corner;
        }));
        return;
      }
      if (corner && !foxyAtCorner.current) {
        foxyAtCorner.current = true;
        moveTo('foxy', tf, 'hop', 'B7', undefined, foxyState, 'to her corner');
      } else if (!corner && foxyAtCorner.current) {
        foxyAtCorner.current = false;
        moveTo('foxy', tf, 'hop', 'B6', undefined, foxyState, 'back to the greeting');
      } else if (!corner) {
        // the greeting spot (steps 1–4) or the pair (5–6): a precise re-dock within a hair; the composing hop into the pair slot is B6
        const cur = pos.current.foxy;
        const d = Math.hypot(cur.x - tf.x, cur.y - tf.y);
        if (d > 24 && pair) moveTo('foxy', tf, 'hop', 'B6', undefined, foxyState, 'into the pair');
        else { if (d > 2) dock('foxy', tf.host, tf.x, tf.y); patch('foxy', { state: foxyState }); }
      } else patch('foxy', { state: foxyState });
    };
    const sayFoxyLine = () => { const hold = welcomeUntil.current - performance.now(); if (hold > 0) wait(hold, () => say('foxy', info.step.foxySay, DUR.m)); else say('foxy', info.step.foxySay, DUR.m); };
    const stays = c.stay && kind === 'hop';
    if (stays) {
      // through the queue: a stay step reached while a hop is still in the air waits for the landing (a direct dock
      // mid-flight would freeze him at a layer coordinate inside the pane and record that as his spot)
      enqueue('clippy', (done) => {
        dock('clippy', tc.host, pos.current.clippy.x, pos.current.clippy.y);
        patch('clippy', { state: clippyState });
        landed();
        foxyBeats();
        done();
      });
      sayFoxyLine();
      return;
    }
    moveTo('clippy', tc, kind, beat, () => {
      landed();
      foxyBeats();
    }, clippyState);
    sayFoxyLine();
  }, [applyStepViews, cue, dock, emote, enqueue, exit, foxyMounted, ledgerTargets, moveTo, oneShot, patch, say, unmountFoxy, wait, whenFoxyMounted]);
  playStepRef.current = playStep;
  greetRef.current = () => {
    const info = lastInfo.current;
    if (!info || inGutter.current || welcomed.current) return;
    welcomed.current = true;
    if (foxyMounted) {
      // the arrival landed mid-ledger and the continuation already summoned her (B6): she still greets him once
      logBeat('B2', 'Foxy greets him (already up via the continuation)', 'fade');
      say('foxy', 'Welcome.');
      welcomeUntil.current = performance.now() + DUR.xxl + DUR.m;
      return;
    }
    const { foxy: tf, pair } = ledgerTargets(info);
    setFoxyMounted(true);
    after(16, () => whenFoxyMounted(() => {
      logBeat('B2', 'Foxy fades up at the greeting spot', 'fade');
      dock('foxy', tf.host, tf.x, tf.y);
      if (nodes.current.foxy) void getComputedStyle(nodes.current.foxy).opacity; // resolve the hidden style first, so the unhide is a fade, not a jump
      patch('foxy', { hidden: false, state: 'listening', facing: 'left', size: pair ? SIZES.foxy.pair : SIZES.foxy.frame, eyes: { mode: 'look', target: { x: -1, y: 0.3 }, key: 'greet' }, act: 'session', docked: true });
      say('foxy', 'Welcome.');
      welcomeUntil.current = performance.now() + DUR.xxl + DUR.m;
      foxyAtCorner.current = !pair && info.index >= 4;
    }));
  };

  // ── ownership (eye beats): the most visible section ≥ 40%, or the ledger while its pane is ≥ 60% ──
  useEffect(() => {
    const ios: IntersectionObserver[] = [];
    const ratios = visibleRatios.current;
    const heights = new Map<string, number>();
    let pick = 0;
    for (const s of SECTIONS) {
      const el = document.querySelector(s.sel);
      if (!el) continue;
      const io = new IntersectionObserver(([e]) => {
        const now = e.isIntersecting ? e.intersectionRatio : 0;
        ratios.set(s.sel, now);
        heights.set(s.sel, e.isIntersecting ? e.intersectionRect.height : 0);
        if (now < 0.4) eyeBeatDone.current.delete(s.sel);
        // one voice at a time: the most visible section ≥ 40% owns his eyes (the larger visible height on a tie);
        // the owner's content comes alive on taking ownership (`fx-guide-owner`, see useSectionEnter). Picked once
        // per frame, after every section's observer has reported, so a section never owns on a stale neighbour
        if (!pick) pick = requestAnimationFrame(() => {
          pick = 0;
          let owner: string | null = null;
          for (const k of SECTIONS) {
            const r = ratios.get(k.sel) ?? 0;
            if (r < 0.4) continue;
            if (!owner || r > (ratios.get(owner) ?? 0) + 1e-6 || (Math.abs(r - (ratios.get(owner) ?? 0)) <= 1e-6 && (heights.get(k.sel) ?? 0) > (heights.get(owner) ?? 0))) owner = k.sel;
          }
          // at the page's end the invitation owns him (the FAQ above it is fully in view too, and would win on height)
          if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2 && (ratios.get('.fx-invite') ?? 0) >= 0.4) owner = '.fx-invite';
          if (owner && owner !== lastSection.current) {
            lastSection.current = owner;
            document.dispatchEvent(new CustomEvent('fx-guide-owner', { detail: owner }));
            if (!ledgerOwns.current) { setOwnerKey(owner); sectionBeat.current(owner); }
          }
        });
      }, { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] });
      io.observe(el);
      ios.push(io);
    }
    return () => { ios.forEach((io) => io.disconnect()); if (pick) cancelAnimationFrame(pick); };
  }, []);
  const eyeBeat = useCallback((section: string) => {
    if (eyeBeatDone.current.has(section)) return;
    eyeBeatDone.current.add(section);
    const s = SECTIONS.find((x) => x.sel === section);
    if (s) logBeat(s.beat, 'eyes');
  }, []);
  useEffect(() => {
    if (!ownerKey || !arrived.current) return;
    const id = after(80, () => {
      if (ownerKey === 'ledger') {
        if (lastInfo.current) playStep(lastInfo.current);
        return;
      }
    });
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerKey]);
  // the stage's own eye beats (the sections drive B14, B15, B16, B20 from their moving parts)
  sectionBeat.current = (ownerKey: string) => {
    if (!arrived.current) return;
    after(80, () => {
      if (floatRef.current.hidden) { pendingOwner.current = ownerKey; return; } // B13 still in the air: replayed on landing
      pendingOwner.current = null;
      if (ownerKey === '.fx-how') {
        eyeBeat(ownerKey); // the stepper lights its steps and drives his eyes (B17, HowItWorks.tsx)
      } else if (ownerKey === '.fx-two') {
        eyeBeat(ownerKey);
        patchFloat({ eyes: { mode: 'half-lid', target: { x: 1, y: 0.1 }, key: 'gtm' } });
      } else if (ownerKey === '.fx-ent') {
        eyeBeat(ownerKey);
        patchFloat({ eyes: { mode: 'neutral', key: 'ent' } });
      } else if (ownerKey === '.fx-invite') {
        eyeBeat(ownerKey);
        const cta = document.querySelector('.fx-invite .button-primary');
        const me = whereInLayer('clippy');
        const l = layer.current!.getBoundingClientRect();
        const b = cta?.getBoundingClientRect();
        const t = b ? unit(b.left - l.left + b.width / 2 - me.x, b.top - l.top + b.height / 2 - me.y) : { x: -1, y: 0 };
        patchFloat({ eyes: { mode: 'wide', target: t, key: 'cta-wide' } });
        wait(DUR.l, () => patchFloat({ eyes: { mode: 'look', target: t, key: 'cta-look' } }));
        if (!emoted.current.has('.fx-invite:pop')) { // the pop and the nod once per load; the wide look on every visit
          emoted.current.add('.fx-invite:pop'); emoted.current.add('.fx-invite:nod');
          wait(DUR.l + DUR.s, () => { setFloatView((v) => ({ ...v, motion: ['react-pop'] })); wait(DUR.s + DUR.l, () => setFloatView((v) => ({ ...v, motion: [] }))); }); // one wind-up-and-pop
          wait(DUR.l + DUR.s + DUR.s + DUR.l + DUR.xs, () => { patchFloat({ emote: 'nod' }); wait(DUR.m * 2, () => patchFloat({ emote: null })); }); // then ONE small nod. End.
        }
      }
    });
  };

  // ── tosses: Foxy flicks a destination chip toward a revealed artifact (inside the ledger only) ──
  const toss = useCallback((text: string, dir: Destination, toEl: HTMLElement) => {
    if (!layer.current || !foxyMounted || viewsRef.current.foxy.hidden) return;
    const run = () => {
      // the chip lives in the artifact column's own (sticky) frame, so a scroll mid-flight cannot pull the card away from it
      const host = (toEl.closest('.fx-arts') as HTMLElement | null) ?? layer.current!;
      const h = host.getBoundingClientRect();
      const fl = whereInLayer('foxy');
      const l = layer.current!.getBoundingClientRect();
      const from = { x: fl.x + l.left - h.left, y: fl.y + l.top - h.top };
      const r = toEl.getBoundingClientRect();
      const to = { x: r.left - h.left, y: r.top - h.top + 11 }; // the chip's box lands on the card's top-left corner
      const chip = document.createElement('span');
      chip.className = `fx-token${dir === 'jira' ? ' dir' : ''}`;
      chip.textContent = text;
      chip.setAttribute('aria-hidden', 'true');
      host.appendChild(chip);
      const half = chip.offsetWidth / 2; // one read: the chip is centred on Foxy, then flies by its centre
      to.x += half;
      chip.style.transform = `translate(${from.x - half}px, ${from.y - 11}px)`; // placed on Foxy before the first frame
      oneShot('foxy', 'flick', DUR.s);
      cue('tick', 'foxy');
      if (reduced) {
        chip.style.transform = `translate(${to.x - half}px, ${to.y - 11}px)`;
        chip.style.transition = 'opacity 280ms';
        chip.style.opacity = '0';
        requestAnimationFrame(() => { chip.style.opacity = '0.9'; });
        after(DUR.xl, () => { chip.style.opacity = '0'; after(DUR.m, () => chip.remove()); });
        return;
      }
      fly({
        from, to, ms: DUR.xl, lift: 60, drift: 0, rFrom: 0, rTo: 180, ease: 'out',
        onFrame: (q, t) => {
          chip.style.transform = `translate(${q.x - half}px, ${q.y - 11}px) rotate(${q.r}deg)`;
          chip.style.opacity = `${t > 1 - 150 / DUR.xl ? 0.9 * (1 - t) / (150 / DUR.xl) : 0.9}`;
        },
        onDone: () => chip.remove(),
      });
    };
    const start = () => {
      if (!foxyMounted || viewsRef.current.foxy.hidden || !pos.current.foxy.host) return; // she has left: no chip without her
      if (moving.current === 'foxy' || pending.current.some((p) => p.who === 'foxy')) { wait(100, start); return; } // a flick, never a move: wait for her own hop to land
      const busy = emoteUntil.current - performance.now();
      if (busy > 0) wait(busy + 300, run);
      else run();
    };
    wait(DUR.l, start); // the card's own reveal transition settles first, so the chip lands on its final corner
  }, [cue, foxyMounted, oneShot, reduced, wait, whereInLayer]);

  const api = useMemo<StageApi>(() => ({
    ledgerOwns: (owns, info, dir) => {
      if (ledgerOwns.current === owns) return;
      ledgerOwns.current = owns;
      lastInfo.current = info;
      if (owns && !arrived.current) {
        // B1 the first time the pane is ≥ 35% visible: at once while step 1 is the active step, after a 250ms settle
        // otherwise, so a continuing scroll does not catch him mid-air
        after(info.index !== 0 ? 250 : 0, () => { if (ledgerOwns.current) arrive(lastInfo.current ?? info); });
      } else if (!owns && dir === 'down' && arrived.current && !storyEnded.current) {
        // the reader jumped past the ledger before the story got to step 14: B12/B13 at once, as fades
        exit(info, true);
      }
      setOwnerKey(owns ? 'ledger' : lastSection.current ?? '');
    },
    ledgerStep: (info) => {
      lastInfo.current = info;
      if (ledgerOwns.current) playStep(info);
    },
    toss,
    react: (section, who, r) => {
      // one voice at a time: only the section that owns his eyes (the most visible ≥ 40%) may move them — and only
      // once the float is up (he lands before he reacts; a reaction during the exit is dropped, the owner's beat is replayed on landing)
      // a reader's own action (a FAQ opened, the CTA hovered) reaches him whenever its section is ≥ 40% in view
      const allowed = r.user ? (visibleRatios.current.get(section) ?? 0) >= 0.4 : section === lastSection.current;
      if (who !== 'clippy' || floatRef.current.hidden || ledgerOwns.current || !allowed) return;
      // the emotes inside an eye beat (nod, pop, lean back, blink) play once per load per section; the eyes may react again on every visit
      const once = (k: string) => { const key = `${section}:${k}`; if (emoted.current.has(key)) return false; emoted.current.add(key); return true; };
      // while he floats, the reactions land on the float
      eyeBeat(section);
      const p: Partial<CharView> = {};
      if (r.eyes) p.eyes = r.eyes;
      if (r.lookAt !== undefined) {
        if (r.lookAt === null) p.eyes = { mode: 'neutral', key: performance.now() };
        else {
          const fr = floatNode.current!.getBoundingClientRect();
          const l = layer.current!.getBoundingClientRect();
          const me = { x: fr.left - l.left + fr.width / 2, y: fr.top - l.top + fr.height / 2 };
          const t = r.lookAt instanceof Element ? (() => { const b = r.lookAt.getBoundingClientRect(); return { x: b.left - l.left + b.width / 2, y: b.top - l.top + b.height / 2 }; })() : r.lookAt;
          p.eyes = { mode: 'look', target: unit(t.x - me.x, t.y - me.y), key: `${Math.round(t.x)},${Math.round(t.y)}` };
        }
      }
      if (r.leanBack && once('lean')) { p.lean = -4; wait(DUR.l, () => patchFloat({ lean: 0 })); }
      patchFloat(p);
      if (r.nod && once('nod')) { patchFloat({ emote: 'nod' }); wait(DUR.m * 2, () => patchFloat({ emote: null })); }
      if (r.pop && once('pop')) { setFloatView((v) => ({ ...v, motion: [...v.motion.filter((m) => m !== 'react-pop'), 'react-pop'] })); wait(DUR.s + DUR.l, () => setFloatView((v) => ({ ...v, motion: v.motion.filter((m) => m !== 'react-pop') }))); }
      if (r.blink && once('blink')) patchFloat({ eyes: { mode: 'slow-blink', key: `blink-${Math.round(performance.now())}` } });
    },
    owner: () => (ledgerOwns.current ? 'ledger' : lastSection.current ?? ''),
  }), [arrive, exit, eyeBeat, patchFloat, playStep, toss, wait]);

  // Act 4 life: while he floats, a 6px drift (CSS, 4s) plus, every 8–14s at random, one of a wind-up-and-pop,
  // a slow 10° lean and back, or a shake-off — one at a time, on the body only, so his anchor never moves.
  useEffect(() => {
    if (floatView.hidden || reduced) return;
    let t = 0;
    const tick = () => {
      // never two at once: if a one-shot or an emote is running, try again in 2s rather than waiting a whole interval
      if (floatRef.current.emote || floatRef.current.motion.length || draggingRef.current) { t = window.setTimeout(tick, 2000); return; }
      const pick = (['pop', 'lean', 'shake'] as const)[Math.floor(Math.random() * 3)];
      const ms = pick === 'pop' ? DUR.s + DUR.l : pick === 'lean' ? DUR.xxl : DUR.m;
      setFloatView((v) => ({ ...v, motion: [`life-${pick}`] }));
      window.setTimeout(() => setFloatView((v) => ({ ...v, motion: v.motion.filter((m) => m !== `life-${pick}`) })), ms);
      t = window.setTimeout(tick, 8000 + Math.random() * 6000);
    };
    t = window.setTimeout(tick, 4000 + Math.random() * 4000); // the first one sooner, after the landing settles
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floatView.hidden, reduced]);

  // the follow-up email's flight starts once its element is in the DOM
  useEffect(() => {
    const el = mailRef.current;
    const f = mailFlight.current;
    if (!mail || mail.gone || !el || !f) return;
    if (reduced) {
      el.style.transform = `translate(${f.from.x - 22}px, ${f.from.y - 22}px)`;
      const t = after(DUR.xxl, () => setMail((mm) => (mm ? { ...mm, gone: true } : mm)));
      return () => clearTimeout(t);
    }
    return fly({
      from: f.from, to: f.to, ms: DUR.xxl - DUR.s, lift: 0, c1: f.c1, c2: f.c2, rFrom: -8, rTo: 16, ease: 'in',
      onFrame: (q) => { el.style.transform = `translate(${q.x - 22}px, ${q.y - 22}px) rotate(${q.r}deg)`; },
      onDone: () => setMail((mm) => (mm ? { ...mm, gone: true } : mm)),
    });
  }, [mail, reduced]);

  // synthetic voice level while a character speaks (no audio on the placeholder)
  useEffect(() => {
    const speaking = (['clippy', 'foxy'] as Who[]).filter((w) => views[w].state === 'speaking');
    for (const w of ['clippy', 'foxy'] as Who[]) levels[w].current = views[w].state === 'speaking' ? 0.4 : 0;
    if (!speaking.length || reduced) return;
    let id = 0;
    const start = performance.now();
    const tick = () => {
      const v = 0.4 + 0.2 * Math.sin(2 * Math.PI * 2 * ((performance.now() - start) / 1000));
      for (const w of speaking) levels[w].current = w === 'foxy' ? v * 0.7 : v;
      id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [views.clippy.state, views.foxy.state, reduced]);
  useEffect(() => () => { timers.current.forEach(clearTimeout); arrivalTimers.current.forEach(clearTimeout); }, []);

  return (
    <StageContext.Provider value={api}>
      {children}
      <div className="fx-guide-stage" ref={layer} aria-hidden="true">
        <Character who="clippy" view={views.clippy} levelRef={levels.clippy} reduced={reduced} ref={(el) => { nodes.current.clippy = el; }} />
        {foxyMounted && <Character who="foxy" view={views.foxy} levelRef={levels.foxy} reduced={reduced} ref={(el) => { nodes.current.foxy = el; }} />}
        {rail && !floatGone && (
          <GutterFloat rail={rail} narrow={narrow} view={floatView} levelRef={floatLevel} reduced={reduced} railBox={railBox} floatNode={(el) => { floatNode.current = el; }}
            onEyes={(dir) => patchFloat({ eyes: dir ? { mode: 'look', target: dir, key: `drag-${Math.round(dir.x * 10)},${Math.round(dir.y * 10)}` } : { mode: 'neutral', key: 'drag-end' } })}
            onDragging={(active) => { draggingRef.current = active; }}
            onHide={() => { setFloatGone(true); patchFloat({ hidden: true }); floatRef.current = { ...floatRef.current, hidden: true }; }} />
        )}
        {mail && (
          <div ref={mailRef} className={`fx-mail${mail.gone ? ' gone' : ''}`} key={mail.key}>
            <Mail size={44} strokeWidth={1.5} />
            <span>Follow-up · held for review</span>
          </div>
        )}
      </div>
    </StageContext.Provider>
  );
}

export type { Eyes, Persona, PresenterState };
